import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { Loader2, UserPlus } from "lucide-react";

function Field({ label, value, onChange, type = "text", required = true, testId }) {
  return (
    <div>
      <label className="block eyebrow mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-sm focus:border-[#0A0A0A] focus:outline-none"
        data-testid={testId}
      />
    </div>
  );
}

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    store_name: "",
    store_number: "",
    contact_name: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register({
        ...form,
        email: form.email.trim().toLowerCase(),
      });
      navigate("/pending", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#f7f7f6] py-10 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-md p-8 space-y-5"
        data-testid="signup-form"
      >
        <div>
          <img
            src="/logo-mark.png"
            alt="InvoiceIQ"
            className="w-10 h-10 rounded-sm mb-4"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <h1 className="font-display text-2xl font-bold">Request store access</h1>
          <p className="text-xs text-[#525252] mt-1">
            Submit your store details. An admin will review and approve.
          </p>
        </div>
        <Field
          label="Legal entity name"
          value={form.store_name}
          onChange={update("store_name")}
          testId="signup-store_name"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Store number"
            value={form.store_number}
            onChange={update("store_number")}
            testId="signup-store_number"
          />
          <Field
            label="Contact phone"
            value={form.phone}
            onChange={update("phone")}
            testId="signup-phone"
          />
        </div>
        <Field
          label="Contact name"
          value={form.contact_name}
          onChange={update("contact_name")}
          testId="signup-contact_name"
        />
        <Field
          label="Email (sign-in)"
          value={form.email}
          onChange={update("email")}
          type="email"
          testId="signup-email"
        />
        <Field
          label="Password"
          value={form.password}
          onChange={update("password")}
          type="password"
          testId="signup-password"
        />
        {error && (
          <div className="text-xs text-[#DC2626] bg-[#FFF0F0] border border-[#FECACA] rounded-sm px-3 py-2" data-testid="signup-error">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white text-sm font-medium rounded-md hover:bg-[#171717] disabled:opacity-50"
          data-testid="signup-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          Submit request
        </button>
        <div className="text-xs text-[#525252] text-center">
          Already approved?{" "}
          <Link to="/login" className="text-[#0A0A0A] underline">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
